/**
 * 객실 유형 표현의 시설·가구·특징 코드를 운영자 말로 바꾼다. 홈페이지가 같은 키에 쓰는 이름과 맞춘다.
 * 모르는 코드는 원문을 그대로 보여 준다(추측해 번역하지 않는다).
 */
const FEATURE_LABEL: Record<string, string> = {
  // 시설
  floorHeating: '바닥난방',
  heater: '난방기(히터)',
  airConditioner: '에어컨',
  'airConditioner(x2)': '에어컨 (2대)',
  'airConditioner(x3)': '에어컨 (3대)',
  washingMachine: '세탁기',
  microwave: '전자레인지',
  refrigerator: '냉장고',
  miniRefrigerator: '미니 냉장고',
  wifi: '와이파이',
  highlight: '하이라이트',
  airCirculator: '공기순환기',
  // 가구
  table: '책상',
  chair: '의자',
  bed: '침대',
  mattress: '매트리스',
  closet: '옷장',
  storageCabinet: '수납장',
  curtain: '커튼',
  shoeCloset: '신발장',
  pantry: '팬트리',
  coffeeTable: '거실 테이블',
  loungeChair: '거실 의자',
  // 특징
  fullyFurnished: '풀옵션',
};

export const featureLabel = (code: string) => FEATURE_LABEL[code] ?? code;

/**
 * 값 하나를 화면 글자로 편다. 문자열·숫자는 그대로, 배열·객체는 안의 값을 " · "로 잇는다.
 * `label`은 문자열 하나하나에 적용한다(시설·가구·특징 코드를 한글로).
 */
export function businessFact(
  value: unknown,
  label: (text: string) => string = (text) => text,
): string | null {
  const text = (item: unknown): string => {
    if (typeof item === 'string') {
      const trimmed = item.trim();
      return trimmed ? label(trimmed) : '';
    }
    if (typeof item === 'number' && Number.isFinite(item)) return String(item);
    if (Array.isArray(item)) return item.map(text).filter(Boolean).join(' · ');
    if (!item || typeof item !== 'object') return '';
    return Object.values(item).map(text).filter(Boolean).join(' · ');
  };
  return text(value) || null;
}

/**
 * 면적 `area_values.<종류>` — 서버가 값과 단위(`SQM` · `SOURCE_UNSPECIFIED`)를 따로 준다.
 * 단위를 모르면 ㎡·평으로 추정하지 않고 "단위 미확인"이라고 적는다. 모양이 다르면 있는 글자만 편다.
 */
export function areaLabel(value: unknown): string | null {
  if (value === null || typeof value !== 'object' || Array.isArray(value))
    return businessFact(value);
  const { value: amount, unit } = value as { value?: unknown; unit?: unknown };
  const number = businessFact(amount);
  if (number === null) return businessFact(value);
  if (unit === 'SQM') return `${number}㎡`;
  if (unit === 'SOURCE_UNSPECIFIED') return `${number} (단위 미확인)`;
  if (typeof unit === 'string' && unit.trim()) return `${number} (${unit.trim()})`;
  return number;
}
